import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import Skeleton from '../../components/Skeleton';

function VIPMembersContent() {
  const { t } = useTranslation();
  const { T, S } = useTheme();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.getManagerPremiumMemberships('active')
      .then(setMembers)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = members.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (m.client_name || '').toLowerCase().includes(q) ||
      (m.client_phone || '').toLowerCase().includes(q) ||
      (m.branch_name || '').toLowerCase().includes(q);
  });

  return (
    <AdminLayout role="manager">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={S.pageTitle}>{t('vip.members', 'VIP Members')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange }} />
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1.5rem' }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('vip.searchMembers', 'Search by name, phone, or branch...')}
          style={{ ...S.input, maxWidth: '400px', width: '100%' }}
        />
      </div>

      {loading ? <Skeleton count={6} height="200px" /> : filtered.length === 0 ? (
        <p style={{ color: T.muted }}>{t('vip.noMembers', 'No VIP members found')}</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
          {filtered.map(m => (
            <div key={m.id} style={{
              ...S.card,
              padding: '1.5rem',
              borderTop: '3px solid #f59e0b',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#000', fontWeight: 700, fontSize: '1.1rem',
                  flexShrink: 0,
                }}>
                  {(m.client_name || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: T.white, fontSize: '1rem', fontFamily: "'DM Sans', sans-serif" }}>
                    {m.client_name}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: T.muted }}>
                    {t('vip.since', 'VIP since')} {m.activated_at ? new Date(m.activated_at).toLocaleDateString() : '-'} &mdash; {m.branch_name}
                  </div>
                </div>
                <span style={{
                  padding: '0.2rem 0.65rem', borderRadius: '20px',
                  fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                  background: '#22c55e22', color: '#22c55e',
                }}>
                  {t('vip.status.active', 'Active')}
                </span>
              </div>

              {/* Stats Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: T.orange, fontFamily: "'DM Sans', sans-serif" }}>
                    {m.windows_count ?? 0}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: T.muted, textTransform: 'uppercase' }}>
                    {t('vip.windows', 'Windows')}
                  </div>
                </div>
                <div style={{ width: '1px', background: T.border }} />
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#22c55e', fontFamily: "'DM Sans', sans-serif" }}>
                    {m.on_time_rate != null ? `${m.on_time_rate}%` : '-'}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: T.muted, textTransform: 'uppercase' }}>
                    {t('vip.onTime', 'On-Time')}
                  </div>
                </div>
                <div style={{ width: '1px', background: T.border }} />
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: T.white, fontFamily: "'DM Sans', sans-serif" }}>
                    {m.total_spent != null ? `${m.total_spent.toLocaleString()}` : '0'} <span style={{ fontSize: '0.7rem', color: T.muted }}>EGP</span>
                  </div>
                  <div style={{ fontSize: '0.68rem', color: T.muted, textTransform: 'uppercase' }}>
                    {t('vip.totalSpent', 'Total Spent')}
                  </div>
                </div>
              </div>

              {/* Preferred Artist */}
              {m.preferred_artist_name && (
                <div style={{
                  padding: '0.6rem 0.85rem', borderRadius: '8px',
                  background: T.bg, fontSize: '0.8rem', color: T.muted,
                }}>
                  {t('vip.preferred', 'Preferred')}: <span style={{ color: T.orange, fontWeight: 600 }}>{m.preferred_artist_name}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export default function VIPMembers() {
  return <ProtectedRoute requireSalonManager><VIPMembersContent /></ProtectedRoute>;
}
